import { memo, useMemo } from 'react'
import { Box, Chip, Typography, Tooltip } from '@mui/material'
import RouteIcon from '@mui/icons-material/Route'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import EditLocationIcon from '@mui/icons-material/EditLocation'
import BaseNode from './BaseNode'
import { WorkflowNodeData, useWorkflowStore } from '@store/workflowStore'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'

interface RouteNodeProps {
  id: string
  data: WorkflowNodeData
  selected?: boolean
}

function RouteNode(props: RouteNodeProps) {
  const { data } = props
  const nodes = useWorkflowStore(state => state.nodes)
  const config = data.routeNodeConfig

  // Find source and destination node labels
  const sourceNode = useMemo(() =>
    nodes.find(n => n.id === config?.sourceNodeId),
    [nodes, config?.sourceNodeId]
  )
  const destinationNode = useMemo(() =>
    nodes.find(n => n.id === config?.destinationNodeId),
    [nodes, config?.destinationNodeId]
  )

  // Get display labels with sequence numbers
  const getNodeDisplayLabel = (node: typeof sourceNode) => {
    if (!node) return null
    const abbrev = node.data.nodeType === WorkflowNodeType.PoiLocation ? 'L' : '?'
    const seq = node.data.sequenceNumber || '?'
    return `${abbrev}${seq}`
  }

  const sourceLabel = getNodeDisplayLabel(sourceNode)
  const destLabel = getNodeDisplayLabel(destinationNode)

  const hasRoute = config?.routePoints && config.routePoints.length > 0
  const distance = config?.distanceMeters
    ? config.distanceMeters >= 1000
      ? `${(config.distanceMeters / 1000).toFixed(1)}km`
      : `${Math.round(config.distanceMeters)}m`
    : null

  // Get travel mode icon
  const TravelIcon = useMemo(() => {
    if (!config?.travelMode) return DirectionsWalkIcon
    if (config.travelMode.includes('cycling')) return DirectionsBikeIcon
    if (config.travelMode.includes('driving')) return DirectionsCarIcon
    return DirectionsWalkIcon
  }, [config?.travelMode])

  // Get data source icon and label
  const dataSourceInfo = useMemo(() => {
    switch (config?.dataSource) {
      case 'gpx':
        return { icon: <UploadFileIcon sx={{ fontSize: 12 }} />, label: 'GPX' }
      case 'manual':
        return { icon: <EditLocationIcon sx={{ fontSize: 12 }} />, label: 'Manual' }
      default:
        return null
    }
  }, [config?.dataSource])

  return (
    <BaseNode
      {...props}
      icon={<RouteIcon fontSize="small" />}
      headerLabel="Route"
      showTargetHandle={true}
      showSourceHandle={true}
    >
      {/* Route connection info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
        <Tooltip title={sourceNode?.data.label || 'Select source location'}>
          <Chip
            size="small"
            label={sourceLabel || '?'}
            variant={sourceNode ? 'filled' : 'outlined'}
            color={sourceNode ? 'primary' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
          />
        </Tooltip>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>→</Typography>
        <Tooltip title={destinationNode?.data.label || 'Select destination location'}>
          <Chip
            size="small"
            label={destLabel || '?'}
            variant={destinationNode ? 'filled' : 'outlined'}
            color={destinationNode ? 'secondary' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
          />
        </Tooltip>
      </Box>

      {/* Route info badges */}
      {hasRoute && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          <Tooltip title={config?.travelMode?.replace(/-/g, ' ') || 'Travel mode'}>
            <Chip
              size="small"
              icon={<TravelIcon sx={{ fontSize: 12 }} />}
              label={distance}
              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
            />
          </Tooltip>
          {dataSourceInfo && (
            <Chip
              size="small"
              icon={dataSourceInfo.icon}
              label={dataSourceInfo.label}
              variant="outlined"
              color="info"
              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
            />
          )}
        </Box>
      )}

      {/* Warning if not configured */}
      {(!sourceNode || !destinationNode) && (
        <Typography
          variant="caption"
          sx={{ color: 'warning.main', fontSize: '0.65rem', mt: 0.5, display: 'block' }}
        >
          Select locations to configure
        </Typography>
      )}
    </BaseNode>
  )
}

export default memo(RouteNode)
